/**
 * Lucide Icon Registry
 *
 * This file explicitly imports only the Lucide icons used in the codebase.
 * This enables tree-shaking and prevents bundling all 1,400+ icons.
 *
 * To add a new icon:
 * 1. Import it from 'lucide-vue-next'
 * 2. Add it to the lucideIcons map with its kebab-case name as the key
 *
 * `getLucideIconComponent` lowercases names before lookup; keys must match that form (e.g. `layout-grid`
 * or `layoutgrid` alias, not camelCase `layoutGrid` alone).
 */
import {
  Activity,
  AlertCircle,
  LogIn,
  Menu,
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  AppWindow,
  BadgeAlert,
  BadgeCheck,
  BarChart2,
  Book,
  Briefcase,
  Bug,
  Building,
  Building2,
  Cloud,
  Calendar,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  CirclePlus,
  CircleX,
  Clock,
  ClipboardList,
  CloudUpload,
  Copy,
  Database,
  Download,
  EllipsisVertical,
  ExternalLink,
  Eye,
  EyeOff,
  File,
  FileCode,
  FileDown,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  Folder,
  Globe,
  Grid3x3,
  History,
  House,
  Image,
  Inbox,
  Info,
  LayoutDashboard,
  LayoutGrid,
  LayoutPanelTop,
  Layers,
  Link,
  Loader2,
  Lock,
  LogOut,
  MessageCirclePlus,
  MessageSquareQuote,
  MessageSquareWarning,
  Minus,
  MinusCircle,
  Moon,
  Mail,
  Package,
  Palette,
  Pencil,
  Phone,
  Pipette,
  Plug,
  Plus,
  Power,
  PlusCircle,
  RefreshCw,
  RotateCw,
  Route,
  Save,
  ShieldCheck,
  ShieldAlert,
  Crown,
  Search,
  Server,
  Settings,
  Share,
  Share2,
  Shield,
  Smartphone,
  Square,
  SquareMinus,
  SquareX,
  Sun,
  Sunrise,
  Tag,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  Upload,
  Unlink,
  User,
  UserCog,
  Users,
  WrapText,
  Wrench,
  X,
  XCircle,
  Star,
  Feather,
  Sparkles,
} from 'lucide-vue-next';
import type {Component} from 'vue';

/**
 * Map of icon names (kebab-case) to Lucide Vue components.
 * Only icons listed here will be available in the Icon component.
 */
export const lucideIcons = {
  // Navigation
  'arrow-left': ArrowLeft,
  'arrow-left-right': ArrowLeftRight,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'chevron-up': ChevronUp,

  // Actions
  check: Check,
  x: X,
  close: X, // alias
  plus: Plus,
  'plus-circle': PlusCircle,
  pencil: Pencil,
  'trash-2': Trash2,
  trash2: Trash2, // alias without hyphen
  copy: Copy,
  'content-copy': Copy, // alias for MDI compatibility
  download: Download,
  upload: Upload,
  'cloud-upload': CloudUpload,
  search: Search,
  // `refresh` is RotateCw; `refresh-cw` is RefreshCw (different Lucide icons).
  'refresh-cw': RefreshCw,
  refresh: RotateCw,
  share: Share,
  'share-2': Share2,
  link: Link,
  unlink: Unlink,
  'external-link': ExternalLink,
  'shield-check': ShieldCheck,
  'shield-alert': ShieldAlert,
  save: Save,
  crown: Crown,
  plug: Plug,
  route: Route,
  feather: Feather,
  'badge-check': BadgeCheck,
  'badge-alert': BadgeAlert,

  // UI Elements
  'app-window': AppWindow,
  'ellipsis-vertical': EllipsisVertical,
  calendar: Calendar,
  eye: Eye,
  'eye-off': EyeOff,
  settings: Settings,
  'minus-circle': MinusCircle,
  circle: Circle,
  'circle-x': CircleX,
  'x-circle': XCircle,
  'alert-circle': AlertCircle,
  'circle-check': CircleCheck,
  'check-circle': CircleCheck, // alias (e.g. ColorSettings status success)
  'circle-plus': CirclePlus,
  'circle-minus': CircleMinus,
  loader: Loader2,
  'loader-2': Loader2,
  lock: Lock,
  'log-out': LogOut,
  clock: Clock,
  info: Info,
  bug: Bug,
  pipette: Pipette,
  palette: Palette,
  image: Image,
  inbox: Inbox,
  'message-square-warning': MessageSquareWarning,
  'message-square-quote': MessageSquareQuote,
  wrench: Wrench,
  smartphone: Smartphone,
  shield: Shield,
  fingerprint: Fingerprint,
  'fingerprint-pattern': Fingerprint, // alias

  // Theme
  sun: Sun,
  moon: Moon,
  sunrise: Sunrise,

  // Content types
  file: File,
  'file-code': FileCode,
  'file-text': FileText,
  'file-spreadsheet': FileSpreadsheet,
  'file-down': FileDown,
  'wrap-text': WrapText,
  'message-plus': MessageCirclePlus,
  folder: Folder,
  book: Book,
  'clipboard-list': ClipboardList,
  history: History,

  // Layout & Categories
  'layout-panel-top': LayoutPanelTop,
  'layout-dashboard': LayoutDashboard,
  'layout-grid': LayoutGrid,
  layoutgrid: LayoutGrid,
  layoutGrid: LayoutGrid, // legacy camelCase alias; prefer `layout-grid` / `layoutgrid`
  layers: Layers,
  'grid-3x3': Grid3x3,
  globe: Globe,
  house: House,
  home: House,
  mail: Mail,
  menu: Menu,
  phone: Phone,
  power: Power,
  'log-in': LogIn,
  package: Package,
  briefcase: Briefcase,
  database: Database,
  server: Server,
  building: Building,
  'building-2': Building2,
  cloud: Cloud,
  'chart-column-increasing': ChartColumn, // closest match
  'chart-column': ChartColumn,
  'chart-bar-big': BarChart2,
  'bar-chart-2': BarChart2, // alias
  activity: Activity,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  minus: Minus,
  'search-alert': CircleAlert,
  tag: Tag,
  timer: Timer,
  'alert-triangle': AlertTriangle,

  // Selection / checkboxes (Analytics "Select all")
  square: Square,
  'square-minus': SquareMinus,
  'square-x': SquareX,
  star: Star,

  // Users
  user: User,
  'user-cog': UserCog,
  users: Users,
  sparkles: Sparkles,
} as const satisfies Record<string, Component>;

/**
 * Type for available Lucide icon names.
 */
export type LucideIconName = keyof typeof lucideIcons;

/**
 * Get a Lucide icon component by name.
 * Returns undefined if the icon is not in the registry.
 */
export function getLucideIconComponent(name: string): Component | undefined {
  const trimmedName = name.trim().toLowerCase();
  if (trimmedName in lucideIcons) {
    return lucideIcons[trimmedName as LucideIconName];
  }
  return undefined;
}
